import { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import CorrectAnswerDirection from "./CorrectAnswerDirection";

const DIMENSIONS = {
  knowledge_areas: {
    button: "Disciplinas / áreas",
    empty: "Nenhuma disciplina ou área do conhecimento foi encontrada nas questões das provas realizadas.",
    singular: "disciplina/área",
  },
  system_contexts: {
    button: "Conteúdos / sistemas",
    empty: "Nenhum conteúdo ou sistema foi encontrado nas questões das provas realizadas.",
    singular: "conteúdo/sistema",
  },
};

const percent = (value) => `${Number(value || 0).toLocaleString("pt-BR", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})}%`;

const count = (value) => Number(value || 0).toLocaleString("pt-BR");

const shortName = (value, limit = 34) => {
  const text = String(value || "Sem identificação");
  return text.length > limit ? `${text.slice(0, limit - 1)}…` : text;
};

const visibleSegmentLabel = (value) => Number(value || 0) >= 9 ? percent(value) : "";

function DomainTooltip({ active, payload }) {
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;
  return (
    <div className="border border-border bg-background p-4 shadow-xl text-xs min-w-64 max-w-80">
      <div className="font-mono-data font-bold text-sm leading-snug">{row.name}</div>
      <div className="space-y-2 mt-3">
        <div className="flex items-center justify-between gap-5 text-[hsl(var(--success))]">
          <span>Acertos</span><strong>{percent(row.hit_percentage)} · {count(row.hits)}</strong>
        </div>
        <div className="flex items-center justify-between gap-5 text-destructive">
          <span>Erros</span><strong>{percent(row.error_percentage)} · {count(row.errors)}</strong>
        </div>
        <div className="flex items-center justify-between gap-5 text-muted-foreground">
          <span>Em branco</span><strong>{percent(row.blank_percentage)} · {count(row.blank)}</strong>
        </div>
      </div>
      <div className="border-t border-border mt-3 pt-3 text-muted-foreground leading-relaxed">
        {count(row.responses)} resposta(s) · {count(row.questions)} questão(ões) · {count(row.exams)} prova(s)
        {row.students ? ` · ${count(row.students)} aluno(s)` : ""}
      </div>
    </div>
  );
}

function ResultLegend({ payload }) {
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs font-mono-data pt-2">
      {(payload || []).map((entry) => (
        <div key={entry.dataKey} className="flex items-center gap-2">
          <span className="w-3 h-3" style={{ background: entry.color }} />
          <span>{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

function DomainAxisTick({ x, y, payload, interactive, selected, onSelect }) {
  const name = String(payload?.value || "");
  const activate = () => {
    if (interactive && name) onSelect(name);
  };
  return (
    <g
      transform={`translate(${x},${y})`}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? `Detalhar contextos de ${name}` : undefined}
      onClick={activate}
      onKeyDown={(event) => {
        if (interactive && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          activate();
        }
      }}
      style={{ cursor: interactive ? "pointer" : "default" }}
    >
      <text
        x={0}
        y={0}
        dy={4}
        textAnchor="end"
        fill={selected ? "hsl(var(--primary))" : "hsl(var(--foreground))"}
        fontSize={11}
        fontWeight={selected ? 700 : 400}
      >
        {interactive ? `${selected ? "▾" : "›"} ` : ""}{shortName(name)}
      </text>
    </g>
  );
}

function CommentedQuestionList({ questions }) {
  if (!questions?.length) {
    return (
      <div className="border border-dashed border-border p-6 text-sm text-muted-foreground">
        Nenhuma questão comentada realizada nesta área ainda.
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {questions.map((question) => {
        const correct = new Set((question.correct_letters || []).map((value) => String(value).toUpperCase()));
        const outcomeLabel = question.outcome === "hits" ? "Acertou" : question.outcome === "blank" ? "Em branco" : "Errou";
        const outcomeTone = question.outcome === "hits" ? "text-[hsl(var(--success))]" : question.outcome === "blank" ? "text-muted-foreground" : "text-destructive";
        return (
          <article key={question.key} className="border border-border bg-card p-4 md:p-5">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="font-mono-data text-xs text-muted-foreground">
                {question.exam_name} · questão {question.position}
                {question.system_context ? ` · ${question.system_context}` : ""}
              </div>
              <div className={`font-mono-data text-xs font-bold uppercase tracking-wider ${outcomeTone}`}>
                {outcomeLabel}
              </div>
            </div>
            <div className="font-medium leading-relaxed mt-3 whitespace-pre-wrap">
              {question.stem || "Enunciado não disponível."}
            </div>
            {!!question.alternatives?.length && (
              <div className="space-y-2 mt-4">
                {question.alternatives.map((alternative) => {
                  const letter = String(alternative.letter || "").toUpperCase();
                  const selected = letter === String(question.selected_answer || "").toUpperCase();
                  const isCorrect = correct.has(letter) || alternative.is_correct;
                  return (
                    <div
                      key={letter}
                      className={`border px-3 py-2 text-sm ${isCorrect ? "border-[hsl(var(--success))] bg-[hsl(var(--success)/0.08)]" : selected ? "border-destructive bg-destructive/5" : "border-border"}`}
                    >
                      <div className="flex gap-2">
                        <strong className="font-mono-data">{letter})</strong>
                        <span>{alternative.text}</span>
                      </div>
                      {(selected || isCorrect) && (
                        <div className="mt-1 text-[11px] font-mono-data uppercase tracking-wider">
                          {selected ? "Sua resposta" : ""}{selected && isCorrect ? " · " : ""}{isCorrect ? "Resposta correta" : ""}
                        </div>
                      )}
                      {alternative.rationale && (
                        <div className="mt-2 text-xs text-muted-foreground leading-relaxed">{alternative.rationale}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <CorrectAnswerDirection
              alternatives={question.alternatives}
              correctLetters={question.correct_letters}
              className="mt-4 text-sm leading-relaxed"
            />
            {question.explanation && (
              <div className="border-l-4 border-primary bg-primary/5 p-3 mt-4 text-sm leading-relaxed">
                <strong>Comentário:</strong> {question.explanation}
              </div>
            )}
            {question.source_reference && (
              <div className="mt-3 text-xs text-muted-foreground"><strong>Fonte:</strong> {question.source_reference}</div>
            )}
          </article>
        );
      })}
    </div>
  );
}

function AreaContextDrilldown({ area, onClose, individual }) {
  const contexts = area?.contexts || [];
  return (
    <section
      className="border border-primary mt-5 bg-background"
      data-testid="academic-context-drilldown"
      aria-label={`Contextos de ${area?.name || "área selecionada"}`}
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 md:p-5 border-b border-border">
        <div>
          <div className="label-eyebrow">área selecionada · contextos</div>
          <h3 className="font-display text-xl md:text-2xl font-black tracking-tight mt-1">{area?.name}</h3>
          <p className="text-xs text-muted-foreground mt-1">
            {count(contexts.length)} contexto(s) · {count(area?.responses)} resposta(s) na área
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="border border-border bg-card px-3 py-2 text-xs font-mono-data uppercase tracking-wider hover:border-foreground shrink-0 self-start md:self-auto"
        >
          Fechar detalhamento
        </button>
      </div>

      {contexts.length ? (
        <div>
          {contexts.map((context) => (
            <article key={context.name} className="p-4 md:p-5 border-b border-border last:border-b-0">
              <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 sm:gap-4">
                <div className="font-mono-data font-bold leading-snug">{context.name}</div>
                <div className="font-display text-xl font-black text-[hsl(var(--success))] shrink-0">
                  {percent(context.hit_percentage)} de acertos
                </div>
              </div>
              <div
                className="flex h-3 bg-muted overflow-hidden mt-3"
                role="img"
                aria-label={`${context.name}: ${percent(context.hit_percentage)} de acertos, ${percent(context.error_percentage)} de erros e ${percent(context.blank_percentage)} em branco`}
              >
                <div className="bg-[hsl(var(--success))]" style={{ width: `${Number(context.hit_percentage || 0)}%` }} />
                <div className="bg-destructive" style={{ width: `${Number(context.error_percentage || 0)}%` }} />
                <div className="bg-muted-foreground" style={{ width: `${Number(context.blank_percentage || 0)}%` }} />
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-xs font-mono-data">
                <span className="text-[hsl(var(--success))]">{count(context.hits)} acerto(s)</span>
                <span className="text-destructive">{count(context.errors)} erro(s) · {percent(context.error_percentage)}</span>
                <span className="text-muted-foreground">{count(context.blank)} em branco · {percent(context.blank_percentage)}</span>
                <span>{count(context.responses)} resposta(s)</span>
                <span>{count(context.questions)} questão(ões)</span>
                <span>{count(context.exams)} prova(s)</span>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="p-8 text-center text-sm text-muted-foreground">
          Esta área ainda não possui um sistema/contexto cadastrado nas questões realizadas.
        </div>
      )}

      {individual && (
        <div className="border-t border-border p-4 md:p-5">
          <div className="label-eyebrow">questões realizadas</div>
          <h4 className="font-display text-xl font-black mt-1 mb-4">Questões comentadas desta área</h4>
          <CommentedQuestionList questions={area?.commented_questions || []} />
        </div>
      )}
    </section>
  );
}

export default function AcademicDomainChart({
  domains,
  eyebrow = "mapa curricular",
  title = "Desempenho por disciplina e conteúdo",
  description = "Percentuais calculados a partir das questões cadastradas nas provas realizadas.",
  individual = false,
}) {
  const [dimension, setDimension] = useState("knowledge_areas");
  const [selectedAreaName, setSelectedAreaName] = useState("");
  const rows = useMemo(() => domains?.[dimension] || [], [domains, dimension]);
  const selectedArea = useMemo(
    () => (domains?.knowledge_areas || []).find((row) => row.name === selectedAreaName),
    [domains, selectedAreaName],
  );
  const selectArea = (source) => {
    if (dimension !== "knowledge_areas") return;
    const row = source?.payload || source;
    const name = String(row?.name || "");
    if (!name) return;
    setSelectedAreaName((current) => current === name ? "" : name);
  };
  const changeDimension = (key) => {
    setDimension(key);
    if (key !== "knowledge_areas") setSelectedAreaName("");
  };
  const coverage = domains?.coverage || {};
  const totals = useMemo(() => {
    const responses = rows.reduce((sum, row) => sum + Number(row.responses || 0), 0);
    const hits = rows.reduce((sum, row) => sum + Number(row.hits || 0), 0);
    const errors = rows.reduce((sum, row) => sum + Number(row.errors || 0), 0);
    const blank = rows.reduce((sum, row) => sum + Number(row.blank || 0), 0);
    return {
      responses,
      hit: responses ? 100 * hits / responses : 0,
      error: responses ? 100 * errors / responses : 0,
      blank: responses ? 100 * blank / responses : 0,
    };
  }, [rows]);
  const questionCoverage = dimension === "knowledge_areas"
    ? coverage.area_question_count
    : coverage.context_question_count;

  return (
    <section className="border border-border bg-card p-5 md:p-6 mb-6" data-testid="academic-domain-chart">
      <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4">
        <div>
          <div className="label-eyebrow">{eyebrow}</div>
          <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight mt-1">{title}</h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-3xl">{description}</p>
        </div>
        <div className="flex flex-wrap gap-1 shrink-0" role="group" aria-label="Tipo de mapeamento acadêmico">
          {Object.entries(DIMENSIONS).map(([key, option]) => (
            <button
              key={key}
              type="button"
              onClick={() => changeDimension(key)}
              aria-pressed={dimension === key}
              className={`border px-3 py-2 text-xs font-mono-data uppercase tracking-wider transition-colors ${
                dimension === key
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background hover:border-foreground"
              }`}
            >
              {option.button}
            </button>
          ))}
        </div>
      </div>

      {rows.length ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border mt-5">
            {[
              [DIMENSIONS[dimension].singular, rows.length, "text-foreground"],
              ["acertos", percent(totals.hit), "text-[hsl(var(--success))]"],
              ["erros", percent(totals.error), "text-destructive"],
              ["em branco", percent(totals.blank), "text-muted-foreground"],
            ].map(([label, value, tone]) => (
              <div key={label} className="bg-card p-3 md:p-4">
                <div className="label-eyebrow">{label}</div>
                <div className={`font-display text-2xl font-black mt-1 ${tone}`}>{value}</div>
              </div>
            ))}
          </div>

          {dimension === "knowledge_areas" && (
            <div className="mt-4 text-xs font-mono-data text-primary">
              Clique no nome ou na barra de uma área para abrir os acertos por contexto{individual ? " e as questões comentadas realizadas" : ""}.
            </div>
          )}

          <div className="overflow-auto max-h-[720px] mt-5">
            <div style={{ height: `${Math.max(330, rows.length * 48 + 85)}px`, minWidth: "760px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={rows}
                  layout="vertical"
                  margin={{ top: 5, right: 24, left: 8, bottom: 8 }}
                  barCategoryGap={8}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} unit="%" tick={{ fontSize: 10 }} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={240}
                    interval={0}
                    tick={(props) => (
                      <DomainAxisTick
                        {...props}
                        interactive={dimension === "knowledge_areas"}
                        selected={selectedAreaName === props.payload?.value}
                        onSelect={(name) => selectArea({ name })}
                      />
                    )}
                  />
                  <Tooltip content={<DomainTooltip />} cursor={{ fill: "hsl(var(--muted) / 0.45)" }} />
                  <Legend content={<ResultLegend />} />
                  <Bar dataKey="hit_percentage" name="Acertos" stackId="outcome" fill="hsl(var(--success))" isAnimationActive={false} onClick={selectArea} cursor={dimension === "knowledge_areas" ? "pointer" : "default"}>
                    <LabelList dataKey="hit_percentage" position="center" formatter={visibleSegmentLabel} fill="white" fontSize={10} fontWeight={700} />
                  </Bar>
                  <Bar dataKey="error_percentage" name="Erros" stackId="outcome" fill="hsl(var(--destructive))" isAnimationActive={false} onClick={selectArea} cursor={dimension === "knowledge_areas" ? "pointer" : "default"}>
                    <LabelList dataKey="error_percentage" position="center" formatter={visibleSegmentLabel} fill="white" fontSize={10} fontWeight={700} />
                  </Bar>
                  <Bar dataKey="blank_percentage" name="Em branco" stackId="outcome" fill="hsl(var(--muted-foreground))" isAnimationActive={false} onClick={selectArea} cursor={dimension === "knowledge_areas" ? "pointer" : "default"}>
                    <LabelList dataKey="blank_percentage" position="center" formatter={visibleSegmentLabel} fill="white" fontSize={10} fontWeight={700} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {dimension === "knowledge_areas" && selectedArea && (
            <AreaContextDrilldown area={selectedArea} individual={individual} onClose={() => setSelectedAreaName("")} />
          )}

          <div className="border-t border-border mt-4 pt-4 text-xs text-muted-foreground font-mono-data leading-relaxed">
            Base mapeada: {count(coverage.mapped_attempt_count)} de {count(coverage.attempt_count)} correção(ões), {count(coverage.mapped_exam_count)} de {count(coverage.exam_count)} prova(s) e {count(questionCoverage)} questão(ões) com {DIMENSIONS[dimension].singular} cadastrada.
            {individual ? " Foi considerada somente a correção válida mais recente deste aluno em cada prova." : " Para alunos identificados, foi considerada somente a correção válida mais recente de cada prova."}
          </div>
        </>
      ) : (
        <div className="border border-dashed border-border p-10 md:p-14 text-center mt-5">
          <div className="label-eyebrow">sem dados acadêmicos mapeados</div>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl mx-auto">{DIMENSIONS[dimension].empty}</p>
          <p className="text-xs text-muted-foreground mt-2">Provas cadastradas somente com gabarito continuam nos resultados gerais, mas não podem ser separadas por área.</p>
        </div>
      )}
    </section>
  );
}
